import { Form, Row, Col, FormInstance, Select, DatePicker, Input } from 'antd';
import { ClientService } from '../../../../shared/api/ClientService';
import useLocale from '../../../../hooks/useLocale';
import './ApplicationFormTab.scss';
import ChangeTrackerFormItem from '../../../../components/ChangeTrackerFormItem';
import SelectSuffixIcon from '../../../../components/SelectSuffixIcon/SelectSuffixIcon';
import { useEffect, useState } from 'react';
import API from '../../../../utils/api';
import TextArea from 'antd/lib/input/TextArea';
import { DATE_FORMAT2, DATE_FORMAT_PLACEHOLDER } from '../../../../constants/common';
import { antdUtil } from '../../../../utils/antdUtil';
import { FormListFieldData } from 'antd/lib/form/FormList';

interface IProps {
  form: FormInstance<ClientService.AppFormPersonalInfoDto>;
  name: string;
  countryId: string;
  isLocked?: boolean;
}

enum AboriginalOptions {
  NorthAmericanIndianFirstNation = 'NORTH_AMERICAN_INDIAN_FIRST_NATION',
  Metis = 'METIS',
  Inuit = 'INUIT',
}

enum DisabilityOptions {
  CoordinationOrDexterity = 'COORDINATION_OR_DEXTERITY',
  Mobility = 'MOBILITY',
  BlindOrVisualImpairment = 'BLIND_OR_VISUAL_IMPAIRMENT',
  DeafOrHardOfHearing = 'DEAF_OR_HARD_OF_HEARING',
  SpeechImpairment = 'SPEECH_IMPAIRMENT',
  OtherDisability = 'OTHER_DISABILITY_LEARNING_DEVELOPMENTAL',
}

enum VisibleMinorityOptions {
  Black = 'BLACK',
  Chinese = 'CHINESE',
  Filipino = 'FILIPINO',
  Japanese = 'JAPANESE',
  Korean = 'KOREAN',
  SouthAsianEastIndian = 'SOUTH_ASIAN_EAST_INDIAN',
  SoutheastAsian = 'SOUTHEAST_ASIAN',
  NonWhiteWestAsianNorthAfricanOrArab = 'NON_WHITE_WEST_ASIAN_NORTH_AFRICAN_OR_ARAB',
  NonWhiteLatinAmerican = 'NON_WHITE_LATIN_AMERICAN',
  PersonOfMixedOrigin = 'PERSON_OF_MIXED_ORIGIN',
  OtherVisibleMinorityGroup = 'OTHER_VISIBLE_MINORITY_GROUP',
}

const PersonalInformationVoluntarySelfIdentification = ({
  form,
  name,
  countryId,
  isLocked = false,
}: IProps): JSX.Element => {
  const { t, getLocalizedDtoName } = useLocale();
  const { Option } = Select;

  const [genderOptions, setGenderOptions] = useState<ClientService.LookupDto[]>([]);
  const [genderOptionsLoading, setGenderOptionsLoading] = useState<boolean>(false);
  const [educationLevels, setEducationLevels] = useState<ClientService.LookupDto[]>([]);
  const [educationLevelsLoading, setEducationLevelsLoading] = useState<boolean>(false);
  const [answerOptions, setAnswerOptions] = useState<ClientService.LookupDto[]>([]);
  const [answerOptionsLoading, setAnswerOptionsLoading] = useState<boolean>(false);
  const [provinces, setProvinces] = useState<ClientService.LookupDto[]>([]);
  const [provincesLoading, setProvincesLoading] = useState<boolean>(false);
  const [isAboriginal, setIsAboriginal] = useState<ClientService.AnswerEnum | undefined>(undefined);
  const [hasDisability, setHasDisability] = useState<ClientService.AnswerEnum | undefined>(undefined);
  const [isMemberOfMinorityGroup, setIsMemberOfMinorityGroup] = useState<ClientService.AnswerEnum | undefined>(
    undefined
  );
  const [hasDisabilityOther, setHasDisabilityOther] = useState<boolean>(false);
  const [isOtherVisibleMinorityGroup, setIsOtherVisibleMinorityGroup] = useState<boolean>(false);

  useEffect(() => {
    setGenderOptionsLoading(true);
    API.listGenderOptions().then((response) => {
      setGenderOptions(response);
      setGenderOptionsLoading(false);
    });
  }, []);

  useEffect(() => {
    setEducationLevelsLoading(true);
    API.listEducationLevels().then((response) => {
      setEducationLevels(response.filter((option) => option.name !== 'Unknown'));
      setEducationLevelsLoading(false);
    });
  }, []);

  useEffect(() => {
    setAnswerOptionsLoading(true);
    API.listAnswerTypes().then((response) => {
      setAnswerOptions(response);
      setAnswerOptionsLoading(false);
    });
  }, []);

  useEffect(() => {
    setProvincesLoading(true);
    API.listProvinces(countryId).then((response) => {
      setProvinces(response);
      setProvincesLoading(false);
    });
  }, []);

  const updateAnswerFormField = (fieldName: string, row: FormListFieldData, value: ClientService.AnswerEnum) => {
    const existingList = form.getFieldValue(name) || [];
    const updatedEntry = {
      ...existingList[row.name],
      [fieldName]: value,
    };

    const newList = [...existingList];
    newList[row.name] = updatedEntry;

    form.setFieldsValue({ [name]: newList });

    if (fieldName === 'isAboriginal') {
      setIsAboriginal(value);
    } else if (fieldName === 'hasDisability') {
      setHasDisability(value);
    } else if (fieldName === 'isMemberOfMinorityGroup') {
      setIsMemberOfMinorityGroup(value);
    }
  };

  const getSelectedAboriginalOptions = (entry: ClientService.AppFormPersonalInfoSelfIdentificationDto): string[] => {
    const selectedOptions: string[] = [];

    if (entry?.isNorthAmericanIndianFirstNation) {
      selectedOptions.push(AboriginalOptions.NorthAmericanIndianFirstNation);
    }
    if (entry?.isInuit) {
      selectedOptions.push(AboriginalOptions.Inuit);
    }
    if (entry?.isMétis) {
      selectedOptions.push(AboriginalOptions.Metis);
    }

    return selectedOptions;
  };

  const getSelectedDisabilityOptions = (entry: ClientService.AppFormPersonalInfoSelfIdentificationDto): string[] => {
    const selectedOptions: string[] = [];

    if (entry?.hasDisabilityCoordinationOrDexterity) {
      selectedOptions.push(DisabilityOptions.CoordinationOrDexterity);
    }
    if (entry?.hasDisabilityMobility) {
      selectedOptions.push(DisabilityOptions.Mobility);
    }
    if (entry?.hasDisabilityBlindOrVisualImpairment) {
      selectedOptions.push(DisabilityOptions.BlindOrVisualImpairment);
    }
    if (entry?.hasDisabilityDeafOrHardOfHearing) {
      selectedOptions.push(DisabilityOptions.DeafOrHardOfHearing);
    }
    if (entry?.hasDisabilitySpeechImpairment) {
      selectedOptions.push(DisabilityOptions.SpeechImpairment);
    }
    if (entry?.hasDisabilityOther) {
      selectedOptions.push(DisabilityOptions.OtherDisability);
      setHasDisabilityOther(entry?.hasDisabilityOther ?? false);
    } else {
      setHasDisabilityOther(false);
    }

    return selectedOptions;
  };

  const getSelectedMinorityOptions = (entry: ClientService.AppFormPersonalInfoSelfIdentificationDto): string[] => {
    const selectedOptions: string[] = [];

    if (entry?.isBlack) {
      selectedOptions.push(VisibleMinorityOptions.Black);
    }
    if (entry?.isChinese) {
      selectedOptions.push(VisibleMinorityOptions.Chinese);
    }
    if (entry?.isFilipino) {
      selectedOptions.push(VisibleMinorityOptions.Filipino);
    }
    if (entry?.isJapanese) {
      selectedOptions.push(VisibleMinorityOptions.Japanese);
    }
    if (entry?.isKorean) {
      selectedOptions.push(VisibleMinorityOptions.Korean);
    }
    if (entry?.isSouthAsianEastIndian) {
      selectedOptions.push(VisibleMinorityOptions.SouthAsianEastIndian);
    }
    if (entry?.isSoutheastAsian) {
      selectedOptions.push(VisibleMinorityOptions.SoutheastAsian);
    }
    if (entry?.isNonWhiteWestAsianNorthAfricanOrArab) {
      selectedOptions.push(VisibleMinorityOptions.NonWhiteWestAsianNorthAfricanOrArab);
    }
    if (entry?.isNonWhiteLatinAmerican) {
      selectedOptions.push(VisibleMinorityOptions.NonWhiteLatinAmerican);
    }
    if (entry?.isPersonOfMixedOrigin) {
      selectedOptions.push(VisibleMinorityOptions.PersonOfMixedOrigin);
    }
    if (entry?.isOtherVisibleMinorityGroup) {
      selectedOptions.push(VisibleMinorityOptions.OtherVisibleMinorityGroup);
      setIsOtherVisibleMinorityGroup(entry?.isOtherVisibleMinorityGroup ?? false);
    } else {
      setIsOtherVisibleMinorityGroup(false);
    }

    return selectedOptions;
  };

  const handleAboriginalSelect = (formListFieldData: FormListFieldData, values: string[]) => {
    const existingList = form.getFieldValue(name) || [];
    const updatedEntry = {
      ...existingList[formListFieldData.name],
      isNorthAmericanIndianFirstNation: values.includes(AboriginalOptions.NorthAmericanIndianFirstNation),
      isMétis: values.includes(AboriginalOptions.Metis),
      isInuit: values.includes(AboriginalOptions.Inuit),
    };

    const newList = [...existingList];
    newList[formListFieldData.name] = updatedEntry;

    form.setFieldsValue({ [name]: newList });
  };

  const handleDisabilitySelect = (formListFieldData: FormListFieldData, values: string[]) => {
    const existingList = form.getFieldValue(name) || [];
    const updatedEntry = {
      ...existingList[formListFieldData.name],
      hasDisabilityCoordinationOrDexterity: values.includes(DisabilityOptions.CoordinationOrDexterity),
      hasDisabilityMobility: values.includes(DisabilityOptions.Mobility),
      hasDisabilityBlindOrVisualImpairment: values.includes(DisabilityOptions.BlindOrVisualImpairment),
      hasDisabilityDeafOrHardOfHearing: values.includes(DisabilityOptions.DeafOrHardOfHearing),
      hasDisabilitySpeechImpairment: values.includes(DisabilityOptions.SpeechImpairment),
      hasDisabilityOther: values.includes(DisabilityOptions.OtherDisability),
    };

    const newList = [...existingList];
    newList[formListFieldData.name] = updatedEntry;

    form.setFieldsValue({ [name]: newList });
  };

  const handleMinorityGroupSelect = (formListFieldData: FormListFieldData, values: string[]) => {
    const existingList = form.getFieldValue(name) || [];
    const updatedEntry = {
      ...existingList[formListFieldData.name],
      isBlack: values.includes(VisibleMinorityOptions.Black),
      isChinese: values.includes(VisibleMinorityOptions.Chinese),
      isFilipino: values.includes(VisibleMinorityOptions.Filipino),
      isJapanese: values.includes(VisibleMinorityOptions.Japanese),
      isKorean: values.includes(VisibleMinorityOptions.Korean),
      isSouthAsianEastIndian: values.includes(VisibleMinorityOptions.SouthAsianEastIndian),
      isSoutheastAsian: values.includes(VisibleMinorityOptions.SoutheastAsian),
      isNonWhiteWestAsianNorthAfricanOrArab: values.includes(
        VisibleMinorityOptions.NonWhiteWestAsianNorthAfricanOrArab
      ),
      isNonWhiteLatinAmerican: values.includes(VisibleMinorityOptions.NonWhiteLatinAmerican),
      isPersonOfMixedOrigin: values.includes(VisibleMinorityOptions.PersonOfMixedOrigin),
      isOtherVisibleMinorityGroup: values.includes(VisibleMinorityOptions.OtherVisibleMinorityGroup),
    };

    const newList = [...existingList];
    newList[formListFieldData.name] = updatedEntry;

    form.setFieldsValue({ [name]: newList });
  };

  return (
    <Form.List name={name}>
      {(voluntarySelfIdentifications) => (
        <>
          {voluntarySelfIdentifications.map((row, _index) => {
            const data = form.getFieldValue(name)[row.name];
            const isAboriginalField = form.getFieldValue([name, row.name, 'isAboriginal']);
            const hasDisabilityField = form.getFieldValue([name, row.name, 'hasDisability']);
            const isMemberOfMinorityGroupField = form.getFieldValue([name, row.name, 'isMemberOfMinorityGroup']);
            const hasDisabilityOtherField = form.getFieldValue([name, row.name, 'hasDisabilityOther']) ?? false;
            const isOtherVisibleMinorityGroupField =
              form.getFieldValue([name, row.name, 'isOtherVisibleMinorityGroup']) ?? false;

            const enableAboriginalDetails =
              isAboriginalField === ClientService.AnswerEnum.Yes || isAboriginal === ClientService.AnswerEnum.Yes;
            const enableDisabilityDetails =
              hasDisabilityField === ClientService.AnswerEnum.Yes || hasDisability === ClientService.AnswerEnum.Yes;
            const enableMinorityGroupDetails =
              isMemberOfMinorityGroupField === ClientService.AnswerEnum.Yes ||
              isMemberOfMinorityGroup === ClientService.AnswerEnum.Yes;
            const enableDisabilityOtherDetails = hasDisabilityOtherField || hasDisabilityOther;
            const enableOtherVisibleMinorityGroupDetails =
              isOtherVisibleMinorityGroupField || isOtherVisibleMinorityGroup;

            return (
              <>
                <Row gutter={20}>
                  <Col span={8}>
                    <ChangeTrackerFormItem name={[row.name, 'genderId']} label={t.WHAT_IS_YOUR_GENDER} listName={name}>
                      <Select
                        loading={genderOptionsLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                      >
                        {genderOptions?.map((option) => (
                          <Option key={option.id} value={option?.id}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'educationLevelId']}
                      label={t.HIGHEST_EDUCATION_LEVEL_COMPLETED}
                      listName={name}
                    >
                      <Select
                        loading={educationLevelsLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                      >
                        {educationLevels?.map((option) => (
                          <Option key={option.id} value={option?.id}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                </Row>
                <Row gutter={20}>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'isAboriginal']}
                      label={t.ARE_YOU_AN_ABORIGINAL_PERSON}
                      listName={name}
                    >
                      <Select
                        loading={answerOptionsLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                        onChange={(value) => updateAnswerFormField('isAboriginal', row, value)}
                      >
                        {answerOptions?.map((option) => (
                          <Option key={option.enumValue} value={option?.enumValue}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                  {enableAboriginalDetails && (
                    <Col span={16}>
                      <ChangeTrackerFormItem
                        label={t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SPECIFY_THE_GROUP_TO_WHICH_YOU_BELONG_TO}
                        listName={name}
                      >
                        <Select
                          suffixIcon={<SelectSuffixIcon />}
                          size="large"
                          disabled={isLocked}
                          mode="multiple"
                          showSearch={false}
                          showArrow={true}
                          value={getSelectedAboriginalOptions(data) || []}
                          onChange={(values: string[]) => handleAboriginalSelect(row, values)}
                        >
                          {Object.values(AboriginalOptions).map((option) => (
                            <Option key={option} value={option}>
                              {t[option]}
                            </Option>
                          ))}
                        </Select>
                      </ChangeTrackerFormItem>
                    </Col>
                  )}
                </Row>
                <Row gutter={20}>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'hasDisability']}
                      label={t.ARE_YOU_A_PERSON_OF_DISABILITY}
                      listName={name}
                    >
                      <Select
                        loading={answerOptionsLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                        onChange={(value) => updateAnswerFormField('hasDisability', row, value)}
                      >
                        {answerOptions?.map((option) => (
                          <Option key={option.enumValue} value={option?.enumValue}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                  {enableDisabilityDetails && (
                    <Col span={16}>
                      <ChangeTrackerFormItem
                        label={t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SELECT_BOXES_THAT_APPLY_TO_YOU}
                        listName={name}
                      >
                        <Select
                          suffixIcon={<SelectSuffixIcon />}
                          size="large"
                          disabled={isLocked}
                          mode="multiple"
                          showSearch={false}
                          showArrow={true}
                          value={getSelectedDisabilityOptions(data)}
                          onChange={(values: string[]) => handleDisabilitySelect(row, values)}
                        >
                          {Object.values(DisabilityOptions).map((option) => (
                            <Option key={option} value={option}>
                              {t[option]}
                            </Option>
                          ))}
                        </Select>
                      </ChangeTrackerFormItem>
                    </Col>
                  )}
                </Row>
                {enableDisabilityDetails && enableDisabilityOtherDetails && (
                  <Row gutter={20}>
                    <Col span={16}>
                      <ChangeTrackerFormItem
                        name={[row.name, 'disabilityOtherDescription']}
                        label={t.OTHER_DISABILITY_PLEASE_SPECIFY}
                        listName={name}
                        rules={[{ required: hasDisabilityOther }]}
                      >
                        <TextArea size="large" disabled={isLocked} />
                      </ChangeTrackerFormItem>
                    </Col>
                  </Row>
                )}
                <Row gutter={20}>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'isMemberOfMinorityGroup']}
                      label={t.ARE_YOU_A_MEMBER_OF_A_VISIBLE_MINORITY_GROUP}
                      listName={name}
                    >
                      <Select
                        loading={answerOptionsLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                        onChange={(value) => updateAnswerFormField('isMemberOfMinorityGroup', row, value)}
                      >
                        {answerOptions?.map((option) => (
                          <Option key={option.enumValue} value={option?.enumValue}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                  {enableMinorityGroupDetails && (
                    <Col span={16}>
                      <ChangeTrackerFormItem
                        label={
                          t.IF_YOU_WISH_TO_PROVIDE_FURTHER_DETAILS_PLEASE_SELECT_THE_BOXES_THAT_BEST_DESCRIBES_YOUR_ORIGIN
                        }
                        listName={name}
                      >
                        <Select
                          suffixIcon={<SelectSuffixIcon />}
                          size="large"
                          disabled={isLocked}
                          mode="multiple"
                          showSearch={false}
                          showArrow={true}
                          value={getSelectedMinorityOptions(data)}
                          onChange={(values: string[]) => handleMinorityGroupSelect(row, values)}
                        >
                          {Object.values(VisibleMinorityOptions).map((option) => (
                            <Option key={option} value={option}>
                              {t[option]}
                            </Option>
                          ))}
                        </Select>
                      </ChangeTrackerFormItem>
                    </Col>
                  )}
                </Row>
                {enableMinorityGroupDetails && enableOtherVisibleMinorityGroupDetails && (
                  <Row gutter={20}>
                    <Col span={16}>
                      <ChangeTrackerFormItem
                        name={[row.name, 'otherVisibleMinorityGroupDescription']}
                        label={t.OTHER_VISIBLE_MINORITY_GROUP_PLEASE_SPECIFY}
                        listName={name}
                        rules={[{ required: isOtherVisibleMinorityGroup }]}
                      >
                        <TextArea size="large" disabled={isLocked} />
                      </ChangeTrackerFormItem>
                    </Col>
                  </Row>
                )}
                <div className="ApplicationFormTab__sub-header">{t.THE_CONSENT_IS_PROVIDED}</div>
                <Row gutter={20}>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'consentDate']}
                      label={t.DATE}
                      listName={name}
                      rules={[{ required: true }]}
                    >
                      <DatePicker
                        size="large"
                        disabledDate={antdUtil.futureDates2}
                        format={DATE_FORMAT2}
                        placeholder={DATE_FORMAT_PLACEHOLDER}
                        disabled={isLocked}
                      />
                    </ChangeTrackerFormItem>
                  </Col>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'consentCity']}
                      label={t.CITY}
                      listName={name}
                      rules={[{ required: true }]}
                    >
                      <Input size="large" disabled={isLocked} />
                    </ChangeTrackerFormItem>
                  </Col>
                  <Col span={8}>
                    <ChangeTrackerFormItem
                      name={[row.name, 'consentProvinceId']}
                      label={t.PROVINCE}
                      listName={name}
                      rules={[{ required: true }]}
                    >
                      <Select
                        loading={provincesLoading}
                        suffixIcon={<SelectSuffixIcon />}
                        size="large"
                        disabled={isLocked}
                      >
                        {provinces?.map((option) => (
                          <Option key={option.id} value={option?.id}>
                            {getLocalizedDtoName(option)}
                          </Option>
                        ))}
                      </Select>
                    </ChangeTrackerFormItem>
                  </Col>
                </Row>
              </>
            );
          })}
        </>
      )}
    </Form.List>
  );
};

export default PersonalInformationVoluntarySelfIdentification;
